import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import Logo from './Logo';
import { 
  HomeIcon, 
  PlusCircleIcon, 
  ArchiveBoxIcon, 
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon 
} from '@heroicons/react/24/outline';

const Navigation: React.FC = () => {
  const { instance, accounts } = useMsal();
  const location = useLocation();

  const handleLogout = () => {
    instance.logoutPopup().catch((e) => {
      console.error('Logout failed:', e);
    });
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const user = accounts[0];

  const navigationLinks = [
    { to: '/', label: 'Dashboard', icon: HomeIcon, shortLabel: 'Home' },
    { to: '/upload', label: 'Inventar hinzufügen', icon: PlusCircleIcon, shortLabel: 'Neu' },
    { to: '/products', label: 'Inventar', icon: ArchiveBoxIcon, shortLabel: 'Inventar' },
    { to: '/settings/branding', label: 'Einstellungen', icon: Cog6ToothIcon, shortLabel: 'Optionen' }
  ];

  return (
    <>
      {/* Navigation - responsive with icon buttons on mobile */}
      <nav className="bg-pottery-cream shadow-lg border-b border-clay-200">
        <div className="max-w-7xl mx-auto px-2 sm:px-4">
          <div className="flex justify-between h-14 sm:h-16">
            {/* Logo - smaller on mobile */}
            <div className="flex items-center flex-shrink-0">
              <Link to="/" className="flex items-center group">
                <Logo size="sm" className="sm:hidden transition-transform duration-200 group-hover:scale-105" />
                <Logo size="md" className="hidden sm:block transition-transform duration-200 group-hover:scale-105" />
                <div className="ml-2 sm:ml-3 flex flex-col">
                  <span className="text-base sm:text-lg font-semibold text-pottery-earth tracking-wide">
                    Imperfekton
                  </span>
                  <span className="hidden sm:block text-sm font-medium text-clay-600 -mt-1">
                    Inventaragent
                  </span>
                </div>
              </Link>
            </div>

            {/* Mobile Icon Buttons */}
            <div className="md:hidden flex items-center space-x-1">
              {navigationLinks.map((link) => {
                const IconComponent = link.icon;
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`${
                      isActive(link.to) 
                        ? 'bg-clay-100 text-pottery-earth' 
                        : 'text-clay-600 hover:bg-clay-50 hover:text-pottery-earth'
                    } flex flex-col items-center justify-center p-2 rounded-lg transition duration-150 ease-in-out`}
                    title={link.label}
                  >
                    <IconComponent className="h-5 w-5" />
                    <span className="text-[10px] mt-0.5 font-medium">{link.shortLabel}</span>
                  </Link>
                );
              })}
              <button
                onClick={handleLogout}
                className="flex flex-col items-center justify-center p-2 rounded-lg text-clay-600 hover:bg-clay-50 hover:text-pottery-earth transition duration-150 ease-in-out"
                title="Abmelden"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
                <span className="text-[10px] mt-0.5 font-medium">Logout</span>
              </button>
            </div>
            
            {/* Desktop Menu Links */}
            <div className="hidden md:flex md:items-center md:ml-8 md:space-x-6">
              {navigationLinks.map((link) => {
                const IconComponent = link.icon;
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`${
                      isActive(link.to) 
                        ? 'border-clay-600 text-pottery-earth' 
                        : 'border-transparent text-clay-600 hover:border-clay-300 hover:text-pottery-earth'
                    } inline-flex items-center gap-2 px-1 pt-1 border-b-2 text-sm font-medium transition duration-150 ease-in-out`}
                  >
                    <IconComponent className="h-4 w-4" />
                    {link.label}
                  </Link>
                );
              })}
            </div>
            
            {/* Desktop User Info and Logout */}
            <div className="hidden md:flex items-center">
              <div className="flex-shrink-0 flex items-center gap-3">
                <span className="text-sm text-clay-700">
                  {user?.name || user?.username || 'Benutzer'}
                </span>
                <button
                  onClick={handleLogout}
                  className="inline-flex items-center gap-2 bg-clay-600 hover:bg-clay-700 text-pottery-cream px-3 py-1.5 rounded-md text-sm font-medium transition duration-150 ease-in-out shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-clay-500 focus:ring-offset-2"
                >
                  <ArrowRightOnRectangleIcon className="h-4 w-4" />
                  Abmelden
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};

export default Navigation; 